import React, { useEffect, useState } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Table, Input, Alert } from "reactstrap";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import TerminalIssuesService from "../../services/TerminalIssues";
import LocalstorageService from "../../helpers/localstorage-service";
import { CompanyId } from "../../constants";
import "./TerminalIssuesModal.css";

const TerminalIssuesModal = ({ isOpen, toggle, onFixComplete }) => {
    const { t } = useTranslation();
    const companyId = LocalstorageService.getItem(CompanyId);

    const [loading, setLoading] = useState(false);
    const [issues, setIssues] = useState([]);
    const [summary, setSummary] = useState([]);
    const [filter, setFilter] = useState("all");
    const [employees, setEmployees] = useState([]);
    const [locations, setLocations] = useState([]);

    // Fix modal state
    const [fixModalOpen, setFixModalOpen] = useState(false);
    const [selectedIssue, setSelectedIssue] = useState(null);
    const [newValue, setNewValue] = useState("");
    const [fixing, setFixing] = useState(false);

    useEffect(() => {
        if (isOpen) {
            loadIssues();
            loadEmployees();
            loadLocations();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const loadIssues = async () => {
        setLoading(true);
        try {
            const response = await TerminalIssuesService.checkTerminalIssues({ companyId });
            const { issues, summary } = response.data;
            setIssues(issues || []);
            setSummary(summary || []);
        } catch (error) {
            toast.error(t("TERMINAL_ISSUES.LOAD_ERROR"));
        } finally {
            setLoading(false);
        }
    };

    const loadEmployees = async () => {
        try {
            const response = await TerminalIssuesService.getEmployeesForFix({ companyId });
            setEmployees(response.data.dataList || []);
        } catch (error) {
            console.error("Error loading employees:", error);
        }
    };

    const loadLocations = async () => {
        try {
            const response = await TerminalIssuesService.getLocationsForFix({ companyId });
            setLocations(response.data.dataList || []);
        } catch (error) {
            console.error("Error loading locations:", error);
        }
    };

    const openFixModal = (issue) => {
        setSelectedIssue(issue);
        setNewValue(issue.suggestedValue === "0" ? "0" : "");
        setFixModalOpen(true);
    };

    const closeFixModal = () => {
        setFixModalOpen(false);
        setSelectedIssue(null);
        setNewValue("");
    };

    const handleFix = async () => {
        if (!selectedIssue) return;

        // Validation
        if (selectedIssue.issueType === "LOCATION_NO_EMPLOYEE" && !newValue) {
            toast.warning(t("TERMINAL_ISSUES.SELECT_EMPLOYEE"));
            return;
        }
        if (selectedIssue.issueType === "TERMINAL_NO_LOCATION" && !newValue) {
            toast.warning(t("TERMINAL_ISSUES.SELECT_LOCATION"));
            return;
        }

        setFixing(true);
        try {
            const response = await TerminalIssuesService.fixTerminalIssue({
                companyId,
                terminalNumber: selectedIssue.terminalNumber,
                issueType: selectedIssue.issueType,
                newValue: newValue,
            });

            if (response.data.success) {
                toast.success(response.data.message);
                closeFixModal();
                loadIssues();
                if (onFixComplete) onFixComplete();
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            toast.error(t("TERMINAL_ISSUES.FIX_ERROR"));
        } finally {
            setFixing(false);
        }
    };

    const handleFixAll = async () => {
        const autoFixableIssues = issues.filter((i) => i.canAutoFix);
        if (autoFixableIssues.length === 0) {
            toast.info(t("TERMINAL_ISSUES.NO_AUTO_FIX"));
            return;
        }

        setLoading(true);
        let fixed = 0;
        let failed = 0;

        for (const issue of autoFixableIssues) {
            try {
                const response = await TerminalIssuesService.fixTerminalIssue({
                    companyId,
                    terminalNumber: issue.terminalNumber,
                    issueType: issue.issueType,
                    newValue: issue.suggestedValue,
                });

                if (response.data.success) {
                    fixed++;
                } else {
                    failed++;
                }
            } catch (error) {
                failed++;
            }
        }

        toast.info(`${t("TERMINAL_ISSUES.FIXED")}: ${fixed}, ${t("TERMINAL_ISSUES.FAILED")}: ${failed}`);
        loadIssues();
        if (onFixComplete) onFixComplete();
        setLoading(false);
    };

    const filteredIssues = filter === "all" ? issues : issues.filter((i) => i.tableName === filter);

    const getSummaryCount = (tableName) => {
        const item = summary.find((s) => s.tableName === tableName);
        return item ? item.issueCount : 0;
    };

    const getTableBadgeClass = (tableName) => {
        const classMap = {
            Terminal: "badge-terminal",
            Location: "badge-location",
            EmployeeToLocationMap: "badge-employee-map",
            LocationLoadingDay: "badge-loading-day",
            DailyAverageOfLocation: "badge-daily-avg",
            TerminalStatus: "badge-terminal-status",
        };
        return classMap[tableName] || "badge-default";
    };

    const renderFixInput = () => {
        if (!selectedIssue) return null;

        switch (selectedIssue.issueType) {
            case "LOCATION_NO_EMPLOYEE":
            case "EMPLOYEE_MAP_MISSING":
                return (
                    <div className="fix-form-group">
                        <label>{t("TERMINAL_ISSUES.SELECT_EMPLOYEE")}:</label>
                        <Input type="select" value={newValue} onChange={(e) => setNewValue(e.target.value)}>
                            <option value="">{t("TERMINAL_ISSUES.CHOOSE_EMPLOYEE")}</option>
                            {employees.map((emp) => (
                                <option key={emp.id} value={emp.id}>
                                    {emp.fullName}
                                </option>
                            ))}
                        </Input>
                    </div>
                );

            case "TERMINAL_NO_LOCATION":
                return (
                    <div className="fix-form-group">
                        <label>{t("TERMINAL_ISSUES.SELECT_LOCATION")}:</label>
                        <Input type="select" value={newValue} onChange={(e) => setNewValue(e.target.value)}>
                            <option value="">{t("TERMINAL_ISSUES.CHOOSE_LOCATION")}</option>
                            {locations.map((loc) => (
                                <option key={loc.id} value={loc.id}>
                                    {loc.locationName}
                                </option>
                            ))}
                        </Input>
                    </div>
                );

            case "TERMINAL_INACTIVE":
            case "LOCATION_INACTIVE":
                return (
                    <div className="fix-form-group">
                        <label>{t("TERMINAL_ISSUES.NEW_STATUS")}:</label>
                        <Input type="select" value={newValue} onChange={(e) => setNewValue(e.target.value)}>
                            <option value="0">0 - {t("TERMINAL_ISSUES.ACTIVE")}</option>
                            <option value="1">1 - {t("TERMINAL_ISSUES.ACTIVE_SECONDARY")}</option>
                            <option value="-1">-1 - {t("TERMINAL_ISSUES.INACTIVE")}</option>
                        </Input>
                    </div>
                );

            default:
                return (
                    <Alert color="info">
                        {t("TERMINAL_ISSUES.AUTO_FIX_INFO")}: {selectedIssue.suggestedValue}
                    </Alert>
                );
        }
    };

    return (
        <>
            <Modal isOpen={isOpen} toggle={toggle} size="xl" className="terminal-issues-modal">
                <ModalHeader toggle={toggle}>
                    <span className="modal-title-icon">🔧</span>
                    {t("TERMINAL_ISSUES.TITLE")}
                </ModalHeader>
                <ModalBody>
                    {loading ? (
                        <div className="loading-container">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    ) : (
                        <>
                            {/* Summary Cards */}
                            <div className="issues-summary">
                                <div className="summary-card terminal" onClick={() => setFilter("Terminal")}>
                                    <div className="summary-count">{getSummaryCount("Terminal")}</div>
                                    <div className="summary-label">Terminal</div>
                                </div>
                                <div className="summary-card location" onClick={() => setFilter("Location")}>
                                    <div className="summary-count">{getSummaryCount("Location")}</div>
                                    <div className="summary-label">Location</div>
                                </div>
                                <div className="summary-card employee-map" onClick={() => setFilter("EmployeeToLocationMap")}>
                                    <div className="summary-count">{getSummaryCount("EmployeeToLocationMap")}</div>
                                    <div className="summary-label">EmployeeToLocationMap</div>
                                </div>
                                <div className="summary-card loading-day" onClick={() => setFilter("LocationLoadingDay")}>
                                    <div className="summary-count">{getSummaryCount("LocationLoadingDay")}</div>
                                    <div className="summary-label">LocationLoadingDay</div>
                                </div>
                                <div className="summary-card daily-avg" onClick={() => setFilter("DailyAverageOfLocation")}>
                                    <div className="summary-count">{getSummaryCount("DailyAverageOfLocation")}</div>
                                    <div className="summary-label">DailyAverageOfLocation</div>
                                </div>
                                <div className="summary-card terminal-status" onClick={() => setFilter("TerminalStatus")}>
                                    <div className="summary-count">{getSummaryCount("TerminalStatus")}</div>
                                    <div className="summary-label">TerminalStatus</div>
                                </div>
                            </div>

                            {/* Filter Buttons */}
                            <div className="filter-buttons">
                                <button className={`filter-btn ${filter === "all" ? "active" : ""}`} onClick={() => setFilter("all")}>
                                    {t("TERMINAL_ISSUES.ALL")} ({issues.length})
                                </button>
                            </div>

                            {/* Issues Table */}
                            {filteredIssues.length === 0 ? (
                                <Alert color="success" className="no-issues-alert">
                                    ✅ {t("TERMINAL_ISSUES.NO_ISSUES")}
                                </Alert>
                            ) : (
                                <div className="issues-table-container">
                                    <Table responsive hover>
                                        <thead>
                                            <tr>
                                                <th>{t("TERMINAL_ISSUES.TERMINAL_NUMBER")}</th>
                                                <th>{t("TERMINAL_ISSUES.LOCATION_NAME")}</th>
                                                <th>{t("TERMINAL_ISSUES.TABLE")}</th>
                                                <th>{t("TERMINAL_ISSUES.FIELD")}</th>
                                                <th>{t("TERMINAL_ISSUES.CURRENT_VALUE")}</th>
                                                <th>{t("TERMINAL_ISSUES.SUGGESTED_VALUE")}</th>
                                                <th>{t("TERMINAL_ISSUES.ACTION")}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredIssues.map((issue, idx) => (
                                                <tr key={idx}>
                                                    <td>
                                                        <strong>{issue.terminalNumber}</strong>
                                                    </td>
                                                    <td>{issue.locationName || "-"}</td>
                                                    <td>
                                                        <span className={`problem-badge ${getTableBadgeClass(issue.tableName)}`}>{issue.tableName}</span>
                                                    </td>
                                                    <td>{issue.fieldName}</td>
                                                    <td>
                                                        <span className="field-value">{issue.currentValue}</span>
                                                    </td>
                                                    <td>
                                                        <span className="suggested-value">{issue.suggestedValue}</span>
                                                    </td>
                                                    <td>
                                                        <button className="btn-fix" onClick={() => openFixModal(issue)}>
                                                            {t("TERMINAL_ISSUES.FIX")}
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            )}
                        </>
                    )}
                </ModalBody>
                <ModalFooter>
                    {issues.filter((i) => i.canAutoFix).length > 0 && (
                        <Button color="success" onClick={handleFixAll} disabled={loading}>
                            ✅ {t("TERMINAL_ISSUES.FIX_ALL")}
                        </Button>
                    )}
                    <Button color="secondary" onClick={toggle}>
                        {t("TERMINAL_ISSUES.CLOSE")}
                    </Button>
                </ModalFooter>
            </Modal>

            {/* Fix Single Issue Modal */}
            <Modal isOpen={fixModalOpen} toggle={closeFixModal} className="fix-issue-modal">
                <ModalHeader toggle={closeFixModal} className="fix-modal-header">
                    🔧 {t("TERMINAL_ISSUES.FIX_ISSUE")}
                </ModalHeader>
                <ModalBody>
                    {selectedIssue && (
                        <>
                            <div className="fix-info">
                                <div className="fix-info-row">
                                    <span className="fix-info-label">{t("TERMINAL_ISSUES.TERMINAL_NUMBER")}:</span>
                                    <span className="fix-info-value">{selectedIssue.terminalNumber}</span>
                                </div>
                                <div className="fix-info-row">
                                    <span className="fix-info-label">{t("TERMINAL_ISSUES.LOCATION_NAME")}:</span>
                                    <span className="fix-info-value">{selectedIssue.locationName || "-"}</span>
                                </div>
                                <div className="fix-info-row">
                                    <span className="fix-info-label">{t("TERMINAL_ISSUES.TABLE")}:</span>
                                    <span className="fix-info-value">{selectedIssue.tableName}</span>
                                </div>
                                <div className="fix-info-row">
                                    <span className="fix-info-label">{t("TERMINAL_ISSUES.FIELD")}:</span>
                                    <span className="fix-info-value">{selectedIssue.fieldName}</span>
                                </div>
                                <div className="fix-info-row">
                                    <span className="fix-info-label">{t("TERMINAL_ISSUES.CURRENT_VALUE")}:</span>
                                    <span className="fix-info-value current-value">{selectedIssue.currentValue}</span>
                                </div>
                            </div>

                            {renderFixInput()}
                        </>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={closeFixModal}>
                        {t("TERMINAL_ISSUES.CANCEL")}
                    </Button>
                    <Button color="success" onClick={handleFix} disabled={fixing}>
                        {fixing ? t("TERMINAL_ISSUES.FIXING") : t("TERMINAL_ISSUES.SAVE_FIX")}
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    );
};

export default TerminalIssuesModal;